/**
 * Patient Comparison View Component
 *
 * Shows side-by-side comparison of existing Tebra data vs new PDF data.
 * Allows selective updates with checkboxes.
 *
 * Desktop equivalent: gui/patient_comparison_view.py
 *
 * Sections:
 * - Demographics (address & phone only - name/DOB cannot be updated)
 * - Guarantor (select existing or add new from PDF)
 * - Primary Insurance (select existing or add new from PDF)
 * - Secondary Insurance (select existing or add new from PDF)
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import { ArrowBack, Save, PersonAdd } from '@mui/icons-material';
import { usePDFStore } from '../../store/pdfStore';


interface PatientComparisonViewProps {
  fullPatientData: Record<string, any>;
  onBack: () => void;
  onSave: (updateData: any) => void;
  onCreateNew?: () => void;
}

export const PatientComparisonView: React.FC<PatientComparisonViewProps> = ({
  fullPatientData,
  onBack,
  onSave,
  onCreateNew,
}) => {
  const { currentPDF } = usePDFStore();
  const pdf = currentPDF;

  // Use full patient data from GetPatient (has guarantor + insurance) — matches desktop
  const patientData = fullPatientData;

  // State for update selections
  const [updateDemographics, setUpdateDemographics] = useState(false);
  const [selectedGuarantor, setSelectedGuarantor] = useState<string>('existing');
  const [selectedPrimaryInsurance, setSelectedPrimaryInsurance] = useState<string>('existing');
  const [selectedSecondaryInsurance, setSelectedSecondaryInsurance] = useState<string>('existing');

  // Demographics fields from PDF (parsed widget columns)
  const [addressLine1, setAddressLine1] = useState(pdf?.AddressLine1 || '');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState(pdf?.City || '');
  const [state, setState] = useState(pdf?.State || '');
  const [zipCode, setZipCode] = useState(pdf?.ZipCode || '');
  const [homePhone, setHomePhone] = useState(pdf?.HomePhone || '');
  const [mobilePhone, setMobilePhone] = useState(pdf?.MobilePhone || '');
  const [workPhone, setWorkPhone] = useState(pdf?.WorkPhone || '');

  // Guarantor fields from PDF
  const [guarantorName, setGuarantorName] = useState(pdf?.GuarantorName || '');
  const [guarantorRelationship, setGuarantorRelationship] = useState(pdf?.GuarantorRelationship || '');

  // Primary Insurance fields from PDF
  const [primaryCompany, setPrimaryCompany] = useState(pdf?.PrimaryInsuranceCompany || '');
  const [primaryPolicy, setPrimaryPolicy] = useState(pdf?.PrimaryPolicyNumber || '');
  const [primaryGroup, setPrimaryGroup] = useState(pdf?.PrimaryGroupNumber || '');
  const [primarySubscriber, setPrimarySubscriber] = useState(pdf?.PrimarySubscriberName || '');

  // Secondary Insurance fields from PDF
  const [secondaryCompany, setSecondaryCompany] = useState(pdf?.SecondaryInsuranceCompany || '');
  const [secondaryPolicy, setSecondaryPolicy] = useState(pdf?.SecondaryPolicyNumber || '');
  const [secondaryGroup, setSecondaryGroup] = useState(pdf?.SecondaryGroupNumber || '');
  const [secondarySubscriber, setSecondarySubscriber] = useState(pdf?.SecondarySubscriberName || '');

  const handleSave = () => {
    const updateData: any = {
      patient_id: patientData.PatientID,
    };

    // Demographics update
    if (updateDemographics) {
      updateData.demographics = {
        AddressLine1: addressLine1,
        AddressLine2: addressLine2,
        City: city,
        State: state,
        ZipCode: zipCode,
        HomePhone: homePhone,
        MobilePhone: mobilePhone,
        WorkPhone: workPhone,
      };
    }

    // Guarantor update — key matches backend Pydantic model (new_guarantor)
    if (selectedGuarantor === 'new' && guarantorName) {
      updateData.new_guarantor = {
        GuarantorName: guarantorName,
        Relationship: guarantorRelationship,
      };
    }

    // Primary Insurance — field names match InsuranceData Pydantic model and desktop flow
    if (selectedPrimaryInsurance === 'new' && primaryCompany) {
      updateData.new_primary_insurance = {
        CompanyName: primaryCompany,
        PolicyNumber: primaryPolicy,
        PolicyGroupNumber: primaryGroup,
        Insured: primarySubscriber,
      };
    }

    // Secondary Insurance
    if (selectedSecondaryInsurance === 'new' && secondaryCompany) {
      updateData.new_secondary_insurance = {
        CompanyName: secondaryCompany,
        PolicyNumber: secondaryPolicy,
        PolicyGroupNumber: secondaryGroup,
        Insured: secondarySubscriber,
      };
    }

    onSave(updateData);
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} variant="outlined">
          Back to Search
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        Update Patient Information
      </Typography>

      {/* Patient Header */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          Patient: {patientData.LastName}, {patientData.FirstName} {patientData.MiddleName || ''}
        </Typography>
        <Typography variant="body2">
          DOB: {patientData.DOB} | ID: {patientData.PatientID}
        </Typography>
      </Alert>

      {/* Demographics Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Demographics
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Name and Date of Birth cannot be updated
        </Alert>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Existing Address & Phone in Tebra:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {patientData.AddressLine1 || 'N/A'}, {patientData.City || 'N/A'}, {patientData.State || 'N/A'}{' '}
          {patientData.ZipCode || 'N/A'}
          <br />
          Home: {patientData.HomePhone || 'N/A'} | Mobile: {patientData.MobilePhone || 'N/A'} | Work:{' '}
          {patientData.WorkPhone || 'N/A'}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          New Address & Phone from PDF:
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Address Line 1"
              fullWidth
              size="small"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Address Line 2"
              fullWidth
              size="small"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField label="City" fullWidth size="small" value={city} onChange={(e) => setCity(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="State" fullWidth size="small" value={state} onChange={(e) => setState(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Zip Code"
              fullWidth
              size="small"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Home Phone"
              fullWidth
              size="small"
              value={homePhone}
              onChange={(e) => setHomePhone(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Mobile Phone"
              fullWidth
              size="small"
              value={mobilePhone}
              onChange={(e) => setMobilePhone(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Work Phone"
              fullWidth
              size="small"
              value={workPhone}
              onChange={(e) => setWorkPhone(e.target.value)}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={<Checkbox checked={updateDemographics} onChange={(e) => setUpdateDemographics(e.target.checked)} />}
          label="Update Address & Phone"
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Guarantor Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Guarantor
        </Typography>

        <RadioGroup value={selectedGuarantor} onChange={(e) => setSelectedGuarantor(e.target.value)}>
          {/* Existing guarantor — flat dict from get_patient_raw_soap (matches desktop) */}
          {patientData.guarantor ? (
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label={`Keep Existing: ${patientData.guarantor.FirstName || ''} ${patientData.guarantor.LastName || ''}`}
            />
          ) : (
            <FormControlLabel value="existing" control={<Radio />} label="No existing guarantor" />
          )}

          <FormControlLabel value="new" control={<Radio />} label="Add New Guarantor from PDF" />
        </RadioGroup>

        {selectedGuarantor === 'new' && (
          <Box sx={{ mt: 2, pl: 4 }}>
            <TextField
              label="Guarantor Name"
              fullWidth
              size="small"
              value={guarantorName}
              onChange={(e) => setGuarantorName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Relationship"
              fullWidth
              size="small"
              value={guarantorRelationship}
              onChange={(e) => setGuarantorRelationship(e.target.value)}
            />
          </Box>
        )}
      </Paper>

      {/* Primary Insurance Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Primary Insurance
        </Typography>

        <RadioGroup value={selectedPrimaryInsurance} onChange={(e) => setSelectedPrimaryInsurance(e.target.value)}>
          {/* Existing primary insurance — flat dict from get_patient_raw_soap (matches desktop) */}
          {patientData.primary_insurance ? (
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label={`Keep Existing: ${patientData.primary_insurance.CompanyName || ''} - ${patientData.primary_insurance.PlanName || ''} - Policy ${patientData.primary_insurance.PolicyNumber || 'N/A'}`}
            />
          ) : (
            <FormControlLabel value="existing" control={<Radio />} label="No existing primary insurance" />
          )}

          <FormControlLabel
            value="new"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Add Insurance from Facesheet</Typography>
                {(pdf?.PrimaryInsuranceCompany || pdf?.PrimaryPolicyNumber) && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {[
                      pdf?.PrimaryInsuranceCompany,
                      pdf?.PrimaryPolicyNumber && `Policy: ${pdf.PrimaryPolicyNumber}`,
                      pdf?.PrimaryGroupNumber && `Group: ${pdf.PrimaryGroupNumber}`,
                      pdf?.PrimarySubscriberName && `Subscriber: ${pdf.PrimarySubscriberName}`,
                      pdf?.PrimarySubscriberRelationship && `Rel: ${pdf.PrimarySubscriberRelationship}`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            }
          />
        </RadioGroup>

        {selectedPrimaryInsurance === 'new' && (
          <Box sx={{ mt: 2, pl: 4 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Insurance Company"
                  fullWidth
                  size="small"
                  value={primaryCompany}
                  onChange={(e) => setPrimaryCompany(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Policy Number"
                  fullWidth
                  size="small"
                  value={primaryPolicy}
                  onChange={(e) => setPrimaryPolicy(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Group Number"
                  fullWidth
                  size="small"
                  value={primaryGroup}
                  onChange={(e) => setPrimaryGroup(e.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Subscriber Name"
                  fullWidth
                  size="small"
                  value={primarySubscriber}
                  onChange={(e) => setPrimarySubscriber(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Secondary Insurance Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Secondary Insurance
        </Typography>

        <RadioGroup value={selectedSecondaryInsurance} onChange={(e) => setSelectedSecondaryInsurance(e.target.value)}>
          {/* Existing secondary insurance — flat dict from get_patient_raw_soap (matches desktop) */}
          {patientData.secondary_insurance ? (
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label={`Keep Existing: ${patientData.secondary_insurance.CompanyName || ''} - ${patientData.secondary_insurance.PlanName || ''} - Policy ${patientData.secondary_insurance.PolicyNumber || 'N/A'}`}
            />
          ) : (
            <FormControlLabel value="existing" control={<Radio />} label="No Secondary Insurance" />
          )}
          <FormControlLabel
            value="new"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Add Insurance from Facesheet</Typography>
                {(pdf?.SecondaryInsuranceCompany || pdf?.SecondaryPolicyNumber) && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {[
                      pdf?.SecondaryInsuranceCompany,
                      pdf?.SecondaryPolicyNumber && `Policy: ${pdf.SecondaryPolicyNumber}`,
                      pdf?.SecondaryGroupNumber && `Group: ${pdf.SecondaryGroupNumber}`,
                      pdf?.SecondarySubscriberName && `Subscriber: ${pdf.SecondarySubscriberName}`,
                      pdf?.SecondarySubscriberRelationship && `Rel: ${pdf.SecondarySubscriberRelationship}`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            }
          />
        </RadioGroup>

        {selectedSecondaryInsurance === 'new' && (
          <Box sx={{ mt: 2, pl: 4 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Insurance Company"
                  fullWidth
                  size="small"
                  value={secondaryCompany}
                  onChange={(e) => setSecondaryCompany(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Policy Number"
                  fullWidth
                  size="small"
                  value={secondaryPolicy}
                  onChange={(e) => setSecondaryPolicy(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Group Number"
                  fullWidth
                  size="small"
                  value={secondaryGroup}
                  onChange={(e) => setSecondaryGroup(e.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Subscriber Name"
                  fullWidth
                  size="small"
                  value={secondarySubscriber}
                  onChange={(e) => setSecondarySubscriber(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" fullWidth size="large" startIcon={<Save />} onClick={handleSave} color="success">
          Update Patient
        </Button>
        {onCreateNew && (
          <Button variant="outlined" fullWidth size="large" startIcon={<PersonAdd />} onClick={onCreateNew} color="primary">
            Create New Patient
          </Button>
        )}
      </Box>
    </Box>
  );
};
